import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  LogOut, Search, Filter, Trash2, MessageCircle, Mail, ArrowUpRight,
  RefreshCw, X, ChevronDown, ChevronUp,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { BRAND } from '../data/mock';

const STATUS_LABELS = {
  new: { label: 'Novo', color: '#C8FF61' },
  contacted: { label: 'Contatado', color: '#16E0C9' },
  negotiating: { label: 'Negociando', color: '#FFB94B' },
  closed: { label: 'Fechado', color: '#7CFB9A' },
  lost: { label: 'Perdido', color: '#FF6B6B' },
};

export default function Admin() {
  const { user, loading, logout, apiCall } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [openLead, setOpenLead] = useState(null);
  const [notes, setNotes] = useState('');
  const nav = useNavigate();

  useEffect(() => {
    if (!loading && !user) nav('/admin/login');
  }, [user, loading, nav]);

  const fetchData = useCallback(async () => {
    setRefreshing(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (search) params.set('q', search);
      const path = `/leads${params.toString() ? '?' + params.toString() : ''}`;
      const [leadsRes, statsRes] = await Promise.all([
        apiCall('GET', path),
        apiCall('GET', '/leads/stats'),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Falha ao carregar leads.');
    } finally {
      setRefreshing(false);
    }
  }, [apiCall, statusFilter, search]);

  useEffect(() => {
    if (user) fetchData();
  }, [user, fetchData]);

  const updateStatus = async (id, status) => {
    try {
      await apiCall('PATCH', `/leads/${id}`, { status });
      toast.success('Status atualizado.');
      fetchData();
      if (openLead?.id === id) setOpenLead({ ...openLead, status });
    } catch (err) { toast.error('Falha ao atualizar.'); }
  };

  const saveNotes = async () => {
    if (!openLead) return;
    try {
      await apiCall('PATCH', `/leads/${openLead.id}`, { notes });
      toast.success('Anotações salvas.');
      fetchData();
    } catch (err) { toast.error('Falha ao salvar.'); }
  };

  const removeLead = async (id) => {
    if (!window.confirm('Excluir este lead permanentemente?')) return;
    try {
      await apiCall('DELETE', `/leads/${id}`);
      toast.success('Lead excluído.');
      setOpenLead(null);
      fetchData();
    } catch (err) { toast.error('Falha ao excluir.'); }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-[#0A0B0E] flex items-center justify-center text-[#8E8B85] font-mono-label">
        carregando...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0B0E] text-[#F5F1E8]">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-[#0A0B0E]/85 backdrop-blur-xl border-b border-white/8">
        <div className="thesis-container py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-end gap-1 cursor-hover" translate="no">
              <span className="font-display text-[22px] tracking-[-0.06em]">Thesis</span>
              <span className="font-serif-italic text-[#C8FF61] text-[18px] -mb-[2px]">°</span>
            </Link>
            <span className="hidden md:inline-block font-mono-label text-[#8E8B85]">// PAINEL ADMIN</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline-block text-[13px] text-[#8E8B85]">{user.email}</span>
            <button onClick={() => { logout(); nav('/admin/login'); }}
              className="cursor-hover inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 hover:border-white/30 text-[13px] transition-colors">
              <LogOut size={14} /> Sair
            </button>
          </div>
        </div>
      </header>

      {/* Stats */}
      <section className="thesis-container py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <span className="font-mono-label text-[#8E8B85]">(01) — Visão geral</span>
            <h1 className="mt-2 font-display-tight text-[40px] md:text-[56px] leading-none">
              Leads<span className="font-serif-italic kinetic-grad">.</span>
            </h1>
          </div>
          <button onClick={fetchData} disabled={refreshing}
            className="cursor-hover inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-[13px] hover:bg-white/5 transition-colors">
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} /> Atualizar
          </button>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-white/8 rounded-[8px] overflow-hidden border border-white/8">
            <StatCell label="Total" value={stats.total} active={statusFilter === ''} onClick={() => setStatusFilter('')} />
            {Object.entries(STATUS_LABELS).map(([k, info]) => (
              <StatCell key={k}
                label={info.label} value={stats[k] || 0}
                color={info.color}
                active={statusFilter === k}
                onClick={() => setStatusFilter(statusFilter === k ? '' : k)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Filters */}
      <section className="thesis-container pb-4">
        <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
          <div className="flex-1 flex items-center gap-3 glass rounded-full px-5 py-3">
            <Search size={16} className="text-[#8E8B85]" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome, empresa, WhatsApp..."
              className="flex-1 bg-transparent outline-none text-[14px] placeholder:text-[#5C5A55]" />
            {search && (
              <button onClick={() => setSearch('')} className="text-[#8E8B85] hover:text-[#F5F1E8]"><X size={14} /></button>
            )}
          </div>
          <div className="flex items-center gap-2 text-[12px] text-[#8E8B85] font-mono-label">
            <Filter size={12} /> {leads.length} resultado{leads.length !== 1 ? 's' : ''}
          </div>
        </div>
      </section>

      {/* Leads list */}
      <section className="thesis-container pb-20">
        {leads.length === 0 ? (
          <div className="glass rounded-[8px] py-20 text-center">
            <p className="font-display-tight text-[28px] text-[#F5F1E8]">Nenhum lead por aqui ainda.</p>
            <p className="mt-3 text-[14px] text-[#8E8B85]">Os leads do formulário do site aparecerão nesta lista.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {leads.map((l) => (
              <LeadRow key={l.id} lead={l}
                onOpen={() => { setOpenLead(l); setNotes(l.notes || ''); }}
                onUpdateStatus={updateStatus}
                onDelete={removeLead}
              />
            ))}
          </div>
        )}
      </section>

      {/* Drawer */}
      {openLead && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-[#0A0B0E]/70 backdrop-blur-sm" onClick={() => setOpenLead(null)} />
          <aside className="relative w-full max-w-[520px] h-full bg-[#0F1014] border-l border-white/10 overflow-y-auto">
            <div className="sticky top-0 bg-[#0F1014]/95 backdrop-blur-md border-b border-white/8 px-7 py-5 flex items-center justify-between">
              <span className="font-mono-label text-[#C8FF61]">// LEAD</span>
              <button onClick={() => setOpenLead(null)} className="text-[#8E8B85] hover:text-[#F5F1E8]"><X size={20} /></button>
            </div>
            <div className="px-7 py-7">
              <h2 className="font-display-tight text-[36px] leading-none">{openLead.name}</h2>
              <p className="mt-2 text-[14px] text-[#8E8B85]">
                {openLead.company || 'Sem empresa informada'} · {new Date(openLead.created_at).toLocaleString('pt-BR')}
              </p>

              <div className="mt-5 flex flex-wrap gap-2">
                {Object.entries(STATUS_LABELS).map(([k, info]) => (
                  <button key={k}
                    onClick={() => updateStatus(openLead.id, k)}
                    className={`cursor-hover px-3 py-1.5 rounded-full text-[11px] font-medium tracking-wide transition-colors ${
                      openLead.status === k ? 'bg-[#C8FF61] text-[#0A0B0E] border-[#C8FF61]' : 'border border-white/15 hover:border-white/40 text-[#F5F1E8]'
                    } border`}
                    style={openLead.status === k ? { background: info.color, color: '#0A0B0E', borderColor: info.color } : {}}
                  >
                    {info.label}
                  </button>
                ))}
              </div>

              <div className="mt-7 space-y-4">
                <DetailItem label="WhatsApp" value={openLead.whatsapp} />
                <DetailItem label="Empresa" value={openLead.company || '—'} />
                <DetailItem label="Origem" value={openLead.source} />
                {openLead.plan_interest && <DetailItem label="Interesse" value={openLead.plan_interest} />}
                {openLead.message && (
                  <div>
                    <span className="font-mono-label text-[#8E8B85]">MENSAGEM</span>
                    <p className="mt-2 text-[15px] leading-relaxed text-[#F5F1E8] glass rounded-[6px] p-4">{openLead.message}</p>
                  </div>
                )}
              </div>

              <div className="mt-7">
                <span className="font-mono-label text-[#8E8B85]">ANOTAÇÕES INTERNAS</span>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={5}
                  placeholder="Suas anotações sobre este lead..."
                  className="mt-2 w-full glass rounded-[6px] p-4 text-[14px] text-[#F5F1E8] placeholder:text-[#5C5A55] outline-none focus:border-[#C8FF61] resize-none"
                />
                <div className="mt-3 flex flex-wrap gap-3">
                  <button onClick={saveNotes} className="btn-primary cursor-hover">Salvar anotações</button>
                  <a href={`https://wa.me/${openLead.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(`Olá ${openLead.name}, aqui é da Thesis.`)}`}
                    target="_blank" rel="noopener noreferrer" className="btn-secondary cursor-hover">
                    <MessageCircle size={16} /> Abrir WhatsApp
                  </a>
                </div>
              </div>

              <div className="mt-10 pt-6 border-t border-white/8 flex items-center justify-between">
                <span className="text-[12px] font-mono-label text-[#8E8B85]">ID: {openLead.id.slice(0, 8)}…</span>
                <button onClick={() => removeLead(openLead.id)}
                  className="cursor-hover inline-flex items-center gap-2 text-[13px] text-[#FF6B6B] hover:text-[#FF8E8E] transition-colors">
                  <Trash2 size={14} /> Excluir lead
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function StatCell({ label, value, color = '#F5F1E8', active, onClick }) {
  return (
    <button onClick={onClick}
      className={`cursor-hover bg-[#0A0B0E] py-5 px-4 text-left transition-colors hover:bg-[#14151B] ${active ? 'bg-[#14151B]' : ''}`}>
      <span className="font-mono-label text-[#8E8B85]">{label}</span>
      <div className="mt-1 font-display-tight text-[32px] leading-none" style={{ color }}>{value}</div>
    </button>
  );
}

function DetailItem({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-white/8 pb-3">
      <span className="font-mono-label text-[#8E8B85]">{label}</span>
      <span className="text-[15px] text-[#F5F1E8]">{value}</span>
    </div>
  );
}

function LeadRow({ lead, onOpen, onUpdateStatus, onDelete }) {
  const info = STATUS_LABELS[lead.status] || STATUS_LABELS.new;
  return (
    <div className="glass rounded-[8px] hover:border-white/20 transition-colors">
      <button onClick={onOpen} className="cursor-hover w-full flex flex-col md:flex-row items-start md:items-center gap-4 p-5 text-left">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <span className="font-display text-[18px] text-[#F5F1E8] truncate">{lead.name}</span>
            {lead.company && <span className="text-[12px] text-[#8E8B85] truncate">· {lead.company}</span>}
          </div>
          <div className="mt-1 flex items-center gap-3 text-[12px] text-[#8E8B85]">
            <span>{lead.whatsapp}</span>
            <span>·</span>
            <span>{new Date(lead.created_at).toLocaleString('pt-BR')}</span>
          </div>
          {lead.message && (
            <p className="mt-2 text-[13px] text-[#F5F1E8]/70 line-clamp-1">{lead.message}</p>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="px-3 py-1.5 rounded-full text-[11px] font-semibold tracking-wide"
            style={{ background: `${info.color}22`, color: info.color, border: `1px solid ${info.color}55` }}>
            {info.label}
          </span>
          <ArrowUpRight size={18} className="text-[#8E8B85]" />
        </div>
      </button>
    </div>
  );
}
